import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { AlertCircle, Loader2 } from "lucide-react";
import { getRunsIntradayAlgoRunsGet, getSlicesIntradayAlgoSlicesGet } from "@/client-admin";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DeleteAllInnovaAlgoTradesDialog } from "@/components/admin/DeleteAllInnovaAlgoTradesDialog";
import { RunsTable } from "@/components/admin/RunsTable/RunsTable";
import { BuildSlicesDialog } from "@/components/admin/SignalsTable/BuildSlicesDialog";
import { SlicesTable } from "@/components/admin/SlicesTable/SlicesTable";
import { TradingLimitsPanel } from "@/components/admin/TradingLimits/TradingLimitsPanel";
import { TradingLimitsStrip } from "@/components/admin/TradingLimits/TradingLimitsStrip";
import { useIsWorkerPaused, WorkerControls } from "@/components/admin/WorkerControls";
import { DatePicker } from "@/components/DatePicker";
import { RunSummary } from "@/client-admin/types.gen";
import { checkSuperUserPermission, getErrorDetails } from "@/lib/utils";

export const Route = createFileRoute("/_superuser/admin/algo-trading/")({
  component: AlgoTradingRunsPage,
  beforeLoad: () => {
    checkSuperUserPermission("algo-trading");
  },
});

function AlgoTradingRunsPage() {
  const navigate = useNavigate();
  const [deliveryDate, setDeliveryDate] = useState<Date | undefined>(new Date());
  const [tab, setTab] = useState("signals");

  const formattedDate = deliveryDate ? format(deliveryDate, "yyyy-MM-dd") : undefined;
  const runsQueryKey = ["intraday-algo-runs", formattedDate];
  const slicesQueryKey = ["intraday-algo-slices", formattedDate];

  const {
    data: runsData,
    isLoading: runsLoading,
    error: runsError,
  } = useQuery({
    queryKey: runsQueryKey,
    queryFn: () => getRunsIntradayAlgoRunsGet({ deliveryDate: formattedDate }),
    refetchInterval: 60_000,
  });

  const {
    data: slicesData,
    isLoading: slicesLoading,
    error: slicesError,
  } = useQuery({
    queryKey: slicesQueryKey,
    queryFn: () => getSlicesIntradayAlgoSlicesGet({ deliveryDate: formattedDate! }),
    enabled: !!formattedDate,
    refetchInterval: 60_000,
  });

  const handleRunClick = (run: RunSummary) => {
    navigate({
      to: "/admin/algo-trading/signals",
      search: {
        last_update: run.last_update,
        intraday_code: run.intraday_code,
      },
    });
  };

  const canBuild = useIsWorkerPaused();

  const isToday = formattedDate === format(new Date(), "yyyy-MM-dd");
  const algoCount = runsData?.data?.reduce((sum, r) => sum + r.algo_ready, 0) ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Innova Algo Trading</h2>
        <DatePicker value={deliveryDate} onChange={setDeliveryDate} />
      </div>

      <div className="flex items-center gap-4 rounded-lg border bg-card px-3 py-2.5 shadow-sm">
        <WorkerControls />
        {isToday && formattedDate && (
          <>
            <div className="ml-auto" />
            <Separator orientation="vertical" className="h-8" />
            <div className="flex items-center gap-2">
              <BuildSlicesDialog deliveryDate={formattedDate} algoCount={algoCount} canBuild={canBuild} />
              <DeleteAllInnovaAlgoTradesDialog canDelete={canBuild} />
            </div>
          </>
        )}
      </div>

      {formattedDate && (
        <TradingLimitsStrip
          deliveryDate={formattedDate}
          onViewLimits={tab === "limits" ? undefined : () => setTab("limits")}
        />
      )}

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList>
          <TabsTrigger value="signals">Signals</TabsTrigger>
          <TabsTrigger value="slices">Slices</TabsTrigger>
          <TabsTrigger value="limits">Limits</TabsTrigger>
        </TabsList>

        <TabsContent value="signals">
          {runsLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          )}
          {runsError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{getErrorDetails(runsError)}</AlertDescription>
            </Alert>
          )}
          {runsData && <RunsTable rows={runsData.data} onRowClick={handleRunClick} />}
        </TabsContent>

        <TabsContent value="slices">
          {slicesLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          )}
          {slicesError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{getErrorDetails(slicesError)}</AlertDescription>
            </Alert>
          )}
          {slicesData && <SlicesTable rows={slicesData.data} />}
        </TabsContent>

        <TabsContent value="limits">{formattedDate && <TradingLimitsPanel deliveryDate={formattedDate} />}</TabsContent>
      </Tabs>
    </div>
  );
}
